import type { ChineseFoodLookup } from '@/domain/health/boohee'
import type { FoodPhotoRecognizer } from '@/domain/health/foodPhoto'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type { FoodLookup, ManualHealthDataStore } from '@/domain/health/contracts'
import { HealthRepository } from '@/domain/health/HealthRepository'
import type { ShortcutSyncProvider } from '@/infrastructure/db/ShortcutSyncProvider'
import type { AppSettings } from '@/domain/health/types'
import type { BackupService } from '@/infrastructure/backup/BackupService'

export interface AppServices {
  repository: HealthRepository
  manualStore: ManualHealthDataStore
  backupService: BackupService
  sync?: ShortcutSyncProvider
  foodPhoto?: FoodPhotoRecognizer
  chineseFood?: ChineseFoodLookup
  foodLookup?: FoodLookup
  demo?: boolean
}

export interface AppSettingsContextValue {
  settings: AppSettings
  setDisplayWeightUnit(unit: 'kg' | 'lb'): Promise<void>
  reloadSettings(): Promise<void>
}

interface AppProvidersProps {
  services: AppServices
  children: ReactNode
  pathname?: string
}

const DEFAULT_SETTINGS: AppSettings = {
  displayWeightUnit: 'kg',
  schemaVersion: 1,
}

const RepositoryContext = createContext<HealthRepository | undefined>(undefined)
const SyncContext = createContext<ShortcutSyncProvider | undefined>(undefined)
const SettingsContext = createContext<AppSettingsContextValue | undefined>(undefined)
const BackupServiceContext = createContext<BackupService | undefined>(undefined)

function normalizeError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error('设置操作失败')
}

function SettingsProvider({
  manualStore,
  children,
}: {
  manualStore: ManualHealthDataStore
  children: ReactNode
}) {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS)
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(false)
  const settingsRef = useRef(settings)
  const operationRef = useRef(0)
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve())

  const publishSettings = useCallback((next: AppSettings) => {
    settingsRef.current = next
    setSettings(next)
  }, [])

  const reloadSettings = useCallback(async () => {
    if (!mountedRef.current) return
    const operation = ++operationRef.current
    try {
      await writeQueueRef.current.catch(() => undefined)
      const persisted = await manualStore.getSettings()
      if (mountedRef.current && operation === operationRef.current) {
        publishSettings(persisted)
      }
    } catch (cause) {
      if (mountedRef.current && operation === operationRef.current) {
        setError(normalizeError(cause))
      }
    }
  }, [manualStore, publishSettings])

  const setDisplayWeightUnit = useCallback(
    async (unit: 'kg' | 'lb') => {
      if (!mountedRef.current) return
      const operation = ++operationRef.current
      const next = { ...settingsRef.current, displayWeightUnit: unit }
      const write = writeQueueRef.current
        .catch(() => undefined)
        .then(() => manualStore.putSettings(next))
      writeQueueRef.current = write
      try {
        await write
        if (mountedRef.current && operation === operationRef.current) {
          publishSettings(next)
        }
      } catch (cause) {
        if (mountedRef.current && operation === operationRef.current) {
          setError(normalizeError(cause))
        }
      }
    },
    [manualStore, publishSettings],
  )

  useEffect(() => {
    mountedRef.current = true
    writeQueueRef.current = Promise.resolve()
    const operation = ++operationRef.current
    void manualStore
      .getSettings()
      .then((persisted) => {
        if (mountedRef.current && operation === operationRef.current) {
          publishSettings(persisted)
        }
      })
      .catch((cause: unknown) => {
        if (mountedRef.current && operation === operationRef.current) {
          setError(normalizeError(cause))
        }
      })
    return () => {
      mountedRef.current = false
      operationRef.current += 1
    }
  }, [manualStore, publishSettings])

  const value = useMemo(
    () => ({ settings, reloadSettings, setDisplayWeightUnit }),
    [reloadSettings, setDisplayWeightUnit, settings],
  )

  if (error !== null) throw error

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function AppProviders({ services, children, pathname = '/' }: AppProvidersProps) {
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    const sync = services.sync
    if (!sync || /\/(new|edit)$/.test(pathname)) return
    const unsubscribe = sync.subscribe(() => setRevision((value) => value + 1))
    const refresh = () => { if (document.visibilityState === 'visible') void sync.sync() }
    refresh()
    window.addEventListener('online', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => { unsubscribe(); window.removeEventListener('online', refresh); document.removeEventListener('visibilitychange', refresh) }
  }, [services.sync, pathname])
  const repository = useMemo(() => {
    // A new repository identity makes existing query hooks reread without remounting pages.
    void revision
    return services.sync ? new HealthRepository([services.sync], services.manualStore, services.foodLookup, services.foodPhoto, services.chineseFood) : services.repository
  }, [services, revision])
  return (
    <SyncContext.Provider value={services.sync}>
    <RepositoryContext.Provider value={repository}>
      <BackupServiceContext.Provider value={services.backupService}>
        <SettingsProvider manualStore={services.manualStore}>{children}</SettingsProvider>
      </BackupServiceContext.Provider>
    </RepositoryContext.Provider>
    </SyncContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useShortcutSync() { return useContext(SyncContext) }

// This provider module intentionally exports its paired hook as one public API.
// eslint-disable-next-line react-refresh/only-export-components
export function useHealthRepository(): HealthRepository {
  const repository = useContext(RepositoryContext)
  if (repository === undefined) {
    throw new Error('useHealthRepository must be used within AppProviders')
  }
  return repository
}

// This provider module intentionally exports its paired hook as one public API.
// eslint-disable-next-line react-refresh/only-export-components
export function useAppSettings(): AppSettingsContextValue {
  const settings = useContext(SettingsContext)
  if (settings === undefined) {
    throw new Error('useAppSettings must be used within AppProviders')
  }
  return settings
}

// Backup access remains behind the application provider so pages cannot reach the store directly.
// eslint-disable-next-line react-refresh/only-export-components
export function useBackupService(): BackupService {
  const backupService = useContext(BackupServiceContext)
  if (backupService === undefined) {
    throw new Error('useBackupService must be used within AppProviders')
  }
  return backupService
}
